import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { useLocation } from "react-router-dom";

/**
 * Po zmianie TRASY strona wraca na gore, a fokus nie zostaje w pustce.
 *
 * Powod: scroll lezy na dokumencie (patrz AppShell), a react-router go nie resetuje. Nowa
 * strona otwierala sie wiec w polowie, bez tytulu - tak samo po powrocie ze szczegolu
 * wywolania na liste w zakladce Developer (zgloszenie z testu 04.10.2026).
 *
 * REAGUJEMY NA `pathname`, NIE na caly adres. Filtry listy i numer strony paginacji siedza
 * w query stringu, a ich zmiana ma zostac tam, gdzie jest (lista sama przewija sie do swojego
 * poczatku tam, gdzie tego potrzebuje). Ten sam mechanizm obejmuje przyciski wstecz/wprzod
 * przegladarki: to tez zmiana sciezki, a na liscie po powrocie filtry wracaja z adresu.
 *
 * PRZEWIJANIE jest natychmiastowe (`instant`), nie plynne: przejscie na inny widok to skok,
 * nie ruch w obrebie strony, a plynny powrot z dolu dlugiej listy trwalby kilkaset ms przy
 * juz wyrenderowanej nowej tresci. Przy prefers-reduced-motion jest to tez wymog A-6, wiec
 * jedno zachowanie spelnia obie rzeczy i nie ma galezi, ktorej nikt nie przetestuje.
 *
 * FOKUS ruszamy tylko wtedy, gdy zginal (aktywny jest <body>, bo klikniety link albo przycisk
 * razem ze starym widokiem zniknal z drzewa). Wtedy trafia na <main>, od ktorego Tab
 * zaczyna sie w nowej tresci, a czytnik ekranu oglasza zmiane widoku. Jesli fokus jest na
 * elemencie, ktory przezyl zmiane (pozycja menu bocznego, przycisk w naglowku), zostaje tam:
 * zabranie go z menu zmuszalo uzytkownika klawiatury do wracania do niego Shift+Tab-ami.
 *
 * Pierwszego renderu nie liczymy - to wejscie na strone, nie zmiana trasy.
 */
export function useStartStronyPoZmianieTrasy(refGlowny: RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation();
  const poprzednia = useRef<string | null>(null);

  useEffect(() => {
    // Odpiecie od pierwszego renderu: ref trzyma sciezke, na ktorej aplikacja sie otworzyla.
    // Dzieki temu podwojne uruchomienie efektu w StrictMode (dev) tez niczego nie przewija.
    if (poprzednia.current === null || poprzednia.current === pathname) {
      poprzednia.current = pathname;
      return;
    }
    poprzednia.current = pathname;

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    const aktywny = document.activeElement;
    if (aktywny === null || aktywny === document.body || !aktywny.isConnected) {
      // preventScroll: pozycje juz ustawilismy wyzej, a focus() bez tej flagi mogloby ja ruszyc.
      refGlowny.current?.focus({ preventScroll: true });
    }
  }, [pathname, refGlowny]);
}
